import { httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import type { ExportEcwCardsInput, ExportEcwCardsResult } from '@citizen-walk/shared-types';
import { functions } from '../../lib/firebase';

/**
 * exportEcwCards callable hook — 分析ツールの「気づき」「処方箋」の CSV / JSON 出力
 * (オーナー要望 2026-09-18)。useExportPosts / useExportParticipants と同 pattern。
 */

export type UseExportEcwCardsState =
  | { status: 'idle'; result: null; error: null }
  | { status: 'pending'; result: null; error: null }
  | { status: 'success'; result: ExportEcwCardsResult; error: null }
  | { status: 'error'; result: null; error: Error };

export interface UseExportEcwCardsReturn {
  state: UseExportEcwCardsState;
  call: (input: ExportEcwCardsInput) => Promise<ExportEcwCardsResult | null>;
  reset: () => void;
}

export function useExportEcwCards(): UseExportEcwCardsReturn {
  const [state, setState] = useState<UseExportEcwCardsState>({
    status: 'idle',
    result: null,
    error: null,
  });

  async function call(input: ExportEcwCardsInput): Promise<ExportEcwCardsResult | null> {
    setState({ status: 'pending', result: null, error: null });
    try {
      const fn = httpsCallable<ExportEcwCardsInput, ExportEcwCardsResult>(
        functions,
        'exportEcwCards',
      );
      const res = await fn(input);
      setState({ status: 'success', result: res.data, error: null });
      return res.data;
    } catch (e) {
      const err = e as Error;
      setState({ status: 'error', result: null, error: err });
      return null;
    }
  }

  function reset() {
    setState({ status: 'idle', result: null, error: null });
  }

  return { state, call, reset };
}

/** CSV / JSON content をブラウザ download として起動する (CSV は Excel 向けに BOM 付与)。 */
export function triggerEcwCardsDownload(result: ExportEcwCardsResult): void {
  const mime =
    result.format === 'csv' ? 'text/csv;charset=utf-8' : 'application/json;charset=utf-8';
  const parts = result.format === 'csv' ? ['﻿', result.content] : [result.content];
  const blob = new Blob(parts, { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = result.filename;
  a.click();
  URL.revokeObjectURL(url);
}
